import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useSite, scrollToId, EASE } from "../lib/site";

const MaskedLines = ({ lines, lang }) => (
  <h1 className="f-display text-[15vw] sm:text-7xl lg:text-[7.2rem] leading-[0.98] tracking-tight" data-testid="hero-title">
    {lines.map((line, i) => (
      <span key={`${lang}-${i}`} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
        <motion.span
          className={`block ${i === 2 ? "italic rtl:not-italic text-[var(--gold)]" : ""}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.15 + i * 0.14 }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </h1>
);

const ArchImage = ({ caption }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 0 0)" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.35 }}
          className="relative aspect-[3/4] overflow-hidden rounded-t-full bg-[var(--ink-2)]"
          data-testid="hero-image"
        >
          <motion.img src="/img/hero.jpg" alt="Dubai skyline at dusk" style={{ y: imgY }} className="absolute inset-0 h-[120%] w-full object-cover scale-110 -top-[10%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/70 via-transparent to-transparent" />
        </motion.div>
        <div className="pointer-events-none absolute -inset-3 rounded-t-full border border-[var(--line-gold)] [transform:translateZ(40px)]" />
        <p className="absolute bottom-5 inset-x-0 text-center f-mono text-[11px] tracking-[0.3em] rtl:tracking-normal rtl:text-sm uppercase text-[var(--sand)] [transform:translateZ(60px)]">{caption}</p>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang, isRTL } = useSite();
  const h = t.hero;
  return (
    <section id="top" className="relative min-h-screen pt-32 pb-16 lg:pt-36 overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-40 end-[-10%] h-[600px] w-[600px] rounded-full bg-[var(--gold)]/10 blur-[140px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.p className="eyebrow mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} data-testid="hero-eyebrow">{h.eyebrow}</motion.p>
          <MaskedLines lines={h.lines} lang={lang} />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1, ease: EASE }}
            className="mt-10 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--muted)]"
            data-testid="hero-subtitle"
          >
            {h.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1, ease: EASE }} className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => scrollToId("contact")} data-testid="hero-cta-primary" className="group inline-flex items-center gap-3 rounded-full bg-[var(--gold)] px-7 py-4 text-[var(--ink)] font-medium hover:bg-[var(--sand)] transition-colors duration-300">
              {h.primary}
              <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : "group-hover:translate-x-1"}`} />
            </button>
            <button onClick={() => scrollToId("services")} data-testid="hero-cta-secondary" className="inline-flex items-center gap-3 rounded-full border border-[var(--line)] px-7 py-4 text-[var(--paper)] hover:border-[var(--gold)] transition-colors duration-300">
              {h.secondary}
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 lg:ps-6 max-w-sm w-full mx-auto lg:max-w-none">
          <ArchImage caption={h.caption} />
        </div>
      </div>
      <motion.button
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="hidden lg:flex absolute bottom-8 start-8 items-center gap-3 f-mono text-xs uppercase tracking-[0.3em] rtl:tracking-normal text-[var(--muted)] hover:text-[var(--gold)] transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2 }}><ArrowDown className="w-4 h-4" /></motion.span>
        {h.scroll}
      </motion.button>
    </section>
  );
};
